import { makeStyles } from '@mui/styles';
import { neutral4 } from 'apollo-react/colors';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { handleErrors } from '../../actions/ErrorActions';
import { getEntitiesList } from '../../api/entities-list';
import { getEntityInfo, getFieldTypes, getNewEntityTemplate } from '../../api/entity';
import { getEntityCatalog } from '../../api/forms';
import Loader from '../../components/CustomLoader';
import EditorErrorPage from '../../components/EditorErrorPage';
import Layout from '../../components/Layout';
import { ENTITIES_LIST } from '../../constants/Urls';
import EntityEditor from '../../entity-designer';
import {
  clearEntityEditor,
  loadEntity,
} from '../../entity-designer/actions/entity-designer.actions';
import {
  getCurrentEntityName,
  getEditMode,
  getIsEntityModelLoaded,
} from '../../entity-designer/selectors/entity-designer.selectors';
import { getNewEntityParams } from '../../entity-designer/utils/create-entity.utils';
import {
  modifyEntitiesListItems,
  modifyEntityCatalogListItems,
  modifyEntityModel,
} from '../../entity-designer/utils/entity-designer.utils';
import { hasFetchError } from '../../form-designer/selectors/common.selectors';
import { setFetchErrorProp } from '../../form-designer/utils/actions/form-manager.actions.utils';

const useStyles = makeStyles({
  root: {
    margin: '0 20px',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 10,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  rowLabel: {
    paddingTop: 8,
    paddingRight: 8,
    alignItems: 'center',
    width: '15%',
  },
  rowColumn: {
    flex: 1,
    alignItems: 'center',
    maxWidth: '50%',
  },
  radio: {
    display: 'flex',
    flexDirection: 'row',
  },
  fields: {
    marginTop: 20,
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1,
    letterSpacing: 'normal',
    color: '#000',
    marginLeft: 17,
  },
  loadingContainer: {
    position: 'absolute',
    width: '95%',
    height: '80%',
  },
  loaderTitle: {
    margin: 35,
    color: neutral4,
  },
});

function EntityEditorPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const isFetchError = useSelector(hasFetchError);
  const isEntityModelLoaded = useSelector(getIsEntityModelLoaded);
  const qsParams = qs.parse(location.search) || {};
  const isEditMode = useSelector((state) =>
    getEditMode(state, { isEditMode: qsParams.isEditMode })
  );
  const [fieldTypes, setFieldTypes] = useState([]);
  const [entitiesList, setEntitiesList] = useState([]);
  const [entityCatalogItems, setEntityCatalogItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const entityName = useSelector(getCurrentEntityName);

  useEffect(() => {
    document.title = isEditMode
      ? (entityName && `Entity Editor / ${entityName}`) || 'Entity Editor'
      : (entityName && `Entity Details / ${entityName}`) || 'Entity Details';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName]);

  useEffect(() => {
    const { isNewEntity, import: isImportMode } = qsParams;

    if (isImportMode && !isEntityModelLoaded) {
      history.push(ENTITIES_LIST);
      return;
    }

    const newEntityParams = getNewEntityParams({
      entityName: qsParams.entityName,
      entityType: qsParams.entityType,
      useGenericTable: qsParams.useGenericTable,
      runtimeTable: qsParams.runtimeTable,
      tableName: qsParams.tableName,
    });

    const fetchData = async () => {
      setIsLoading(true);
      const [
        entityInfoResponse,
        fieldTypesResponse,
        entitiesListResponse,
        entityCatalogResponse,
      ] = await Promise.all([
        isNewEntity
          ? getNewEntityTemplate(newEntityParams)
          : !isImportMode &&
            getEntityInfo({ entityName: qsParams.entityName, entityId: qsParams.itmId }),
        getFieldTypes(),
        getEntitiesList(),
        getEntityCatalog(),
      ]);

      if (
        entityInfoResponse?.hasError ||
        fieldTypesResponse.hasError ||
        entitiesListResponse.hasError ||
        entityCatalogResponse.hasError
      ) {
        const entityInfoResponseErrors = setFetchErrorProp(entityInfoResponse.errors);
        const fieldTypesResponseErrors = setFetchErrorProp(fieldTypesResponse.errors);
        const entitiesListResponseErrors = setFetchErrorProp(entitiesListResponse.errors);
        const entityCatalogResponseErrors = setFetchErrorProp(entityCatalogResponse.errors);

        entityInfoResponse?.hasError && dispatch(handleErrors(entityInfoResponseErrors));
        fieldTypesResponse.hasError && dispatch(handleErrors(fieldTypesResponseErrors));
        entitiesListResponse.hasError && dispatch(handleErrors(entitiesListResponseErrors));
        entityCatalogResponse.hasError && dispatch(handleErrors(entityCatalogResponseErrors));
        return;
      }

      setFieldTypes(fieldTypesResponse.fieldTypes);
      setEntitiesList(modifyEntitiesListItems(entitiesListResponse.entities));
      setEntityCatalogItems(
        modifyEntityCatalogListItems(entityCatalogResponse.entityCatalog.Entity)
      );
      !isImportMode &&
        dispatch(
          loadEntity(
            modifyEntityModel(
              !isNewEntity ? entityInfoResponse.EntityModel : entityInfoResponse.newEntityModel
            )
          )
        );
      setIsLoading(false);
    };
    fetchData();

    return () => {
      dispatch(clearEntityEditor());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout dashboardLink={ENTITIES_LIST}>
      {isFetchError ? (
        <EditorErrorPage />
      ) : isLoading ? (
        <div className={classes.loadingContainer}>
          <Loader />
        </div>
      ) : (
        <EntityEditor
          fieldTypes={fieldTypes}
          entitiesList={entitiesList}
          entityCatalogItems={entityCatalogItems}
          isEditMode={isEditMode}
        />
      )}
    </Layout>
  );
}

export default EntityEditorPage;
